// ui/card-selection.js: B3b U5 pure rules for the tap-to-raise lift.
//
// A raised (lifted) card is only meaningful inside the turn that raised
// it. When the phase changes or it is no longer the player's turn,
// nothing is playable, so the lift must clear instead of leaving a card
// stuck raised. The dealer-discard pick clears the same way once the
// discard turn is over. NetworkedGameView drives its local selection
// state through this small reducer so node:test can pin the rules.

/** @typedef {{ raisedCardId: string|null, hoveredCardId: string|null, discardSelection: string|null }} CardSelectionState */

/** @type {CardSelectionState} */
export const INITIAL_SELECTION = Object.freeze({
  raisedCardId: null,
  hoveredCardId: null,
  discardSelection: null,
});

/**
 * @param {CardSelectionState} state
 * @param {{
 *   type: 'raise',
 *   card_id: string,
 * } | {
 *   type: 'sync',
 *   currentState: string|null,
 *   isMyTurn: boolean,
 *   isMyDiscardTurn: boolean,
 *   connectionOpen?: boolean,
 * } | {
 *   type: 'select_discard',
 *   card_id: string|null,
 * }} action
 * @returns {CardSelectionState}
 */
export function cardSelectionReducer(state, action) {
  if (!action || typeof action !== 'object') return state;
  switch (action.type) {
    case 'raise':
      if (typeof action.card_id !== 'string' || action.card_id.length === 0) return state;
      return { ...state, raisedCardId: action.card_id, hoveredCardId: action.card_id };
    case 'select_discard':
      return { ...state, discardSelection: action.card_id ?? null };
    case 'sync': {
      // B3b U5: the lift only survives while it is still the player's
      // trick-play turn; any phase change or lost turn clears the raise.
      // The hovered twin clears with it only when it IS the tap lift
      // (hovered === raised): a genuine desktop mouse hover over another
      // card is live pointer state and stays until mouse-leave. Leaving
      // the discard turn clears the discard pick.
      const stillMyPlayTurn = action.connectionOpen !== false && action.isMyTurn === true && action.currentState === 'trick_play';
      const stillMyDiscardTurn = action.isMyDiscardTurn === true;
      const nextRaised = stillMyPlayTurn ? state.raisedCardId : null;
      const nextHovered = stillMyPlayTurn
        ? state.hoveredCardId
        : (state.hoveredCardId != null && state.hoveredCardId === state.raisedCardId)
          ? null
          : state.hoveredCardId;
      const nextDiscard = stillMyDiscardTurn ? state.discardSelection : null;
      if (nextRaised === state.raisedCardId
        && nextHovered === state.hoveredCardId
        && nextDiscard === state.discardSelection) {
        return state;
      }
      return { raisedCardId: nextRaised, hoveredCardId: nextHovered, discardSelection: nextDiscard };
    }
    default:
      return state;
  }
}

export function syncNetworkedSelection(selection, currentState, isMyTurn, isMyDiscardTurn, connectionOpen) {
  const canPlay = connectionOpen && ((currentState === 'trick_play' && isMyTurn) || isMyDiscardTurn);
  if (!canPlay) return {
    raisedCardId: null,
    hoveredCardId: null,
    discardSelection: null,
  };
  return cardSelectionReducer(selection, {
    type: 'sync', currentState, isMyTurn, isMyDiscardTurn, connectionOpen,
  });
}

/**
 * B3b item 3: what a tap on a legal card does in trick play. With
 * tap-to-raise, the first tap lifts the card (raise + hover twin) and
 * plays nothing; a tap on the already-raised card, or any tap without
 * tap-to-raise, plays it and clears both lifts at once.
 *
 * @param {CardSelectionState} selection
 * @param {string} cardId
 * @param {boolean} tapToRaise
 * @returns {{ selection: CardSelectionState, play: string|null }}
 */
export function cardTapOutcome(selection, cardId, tapToRaise) {
  if (tapToRaise && selection.raisedCardId !== cardId) {
    return { selection: { ...selection, raisedCardId: cardId, hoveredCardId: cardId }, play: null };
  }
  return { selection: { ...selection, raisedCardId: null, hoveredCardId: null }, play: cardId };
}

/**
 * Push a new selection into the view's useState setters, calling only
 * the setters whose value changed.
 *
 * @param {CardSelectionState} prev
 * @param {CardSelectionState} next
 * @param {{ setRaisedCardId: (v: string|null) => void, setHoveredCardId: (v: string|null) => void, setDiscardSelection: (v: string|null) => void }} setters
 */
export function applySelection(prev, next, setters) {
  if (next.raisedCardId !== prev.raisedCardId) setters.setRaisedCardId(next.raisedCardId);
  if (next.hoveredCardId !== prev.hoveredCardId) setters.setHoveredCardId(next.hoveredCardId);
  if (next.discardSelection !== prev.discardSelection) setters.setDiscardSelection(next.discardSelection);
}

/**
 * The NetworkedGameView sync effect body: clear a lift or a discard
 * pick the moment it stops being the player's turn to use it.
 *
 * @param {CardSelectionState} selection
 * @param {{ currentState: string|null, isMyTurn: boolean, isMyDiscardTurn: boolean, connectionOpen: boolean }} inputs
 * @param {Parameters<typeof applySelection>[2]} setters
 */
export function selectionSyncEffect(selection, inputs, setters) {
  const next = syncNetworkedSelection(
    selection, inputs.currentState, inputs.isMyTurn, inputs.isMyDiscardTurn, inputs.connectionOpen);
  applySelection(selection, next, setters);
}
